import {
  ConciliationCaseEvent,
  ConciliationChange,
} from "@/@types/conciliation-history";
import { formatDateTime2 } from "@/utils/format-date";

import { ConciliationCaseOperationGroup, JustificationNames } from "../../types";
import { actorLabelOf } from "../../utils/actor-label";
import { changeLabelOf } from "../../utils/change-label";
import { conciliationEventOf } from "../../utils/group-case-events";
import {
  CASE_TIMELINE_LABELS,
  NOT_AVAILABLE,
  TEXT_SEPARATORS,
} from "../../utils/messages";
import { CaseConciliationBlock } from "./case-conciliation-block";
import {
  CASE_OPERATION_GROUP_TEST_ID,
  CASE_EVENT_TEST_ID,
  CASE_GROUP_DATE_TEST_ID,
} from "../../utils/case-history-test-ids";

interface CaseOperationGroupProps {
  group: ConciliationCaseOperationGroup;
  justificationNames: JustificationNames;
  onOpenCase: (itemId: number) => void;
}

const reasonOf = (event: ConciliationCaseEvent): string | null =>
  event.context.note || null;

interface CaseEventLineProps {
  event: ConciliationCaseEvent;
  justificationNames: JustificationNames;
}

function CaseEventLine({ event, justificationNames }: CaseEventLineProps): JSX.Element {
  const reason: string | null = reasonOf(event);

  return (
    <li className="text-xs text-gray-700" data-testid={CASE_EVENT_TEST_ID}>
      <span className="font-medium">
        {CASE_TIMELINE_LABELS.EVENT[event.event] ?? event.event}
      </span>

      {event.changes.length > 0 && (
        <span>
          {TEXT_SEPARATORS.DETAIL}
          {event.changes
            .map((change: ConciliationChange): string =>
              changeLabelOf(change, justificationNames)
            )
            .join(TEXT_SEPARATORS.CHANGES)}
        </span>
      )}

      {reason && (
        <span className="block whitespace-pre-line break-words">
          <strong>{CASE_TIMELINE_LABELS.REASON}</strong>
          {TEXT_SEPARATORS.WORD}
          {reason}
        </span>
      )}
    </li>
  );
}

export function CaseOperationGroup({
  group,
  justificationNames,
  onOpenCase,
}: CaseOperationGroupProps): JSX.Element {
  const conciliation: ConciliationCaseEvent | null =
    conciliationEventOf(group);

  return (
    <li
      className="border-l-2 border-[#F6D040] pl-3 py-1"
      data-testid={CASE_OPERATION_GROUP_TEST_ID}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium text-sm text-[#613F4F]">
          {CASE_TIMELINE_LABELS.EVENT[group.events[0].event] ??
            group.events[0].event}
        </span>
        <span
          className="text-xs text-gray-500 whitespace-nowrap"
          data-testid={CASE_GROUP_DATE_TEST_ID}
        >
          {formatDateTime2(group.at, { fallback: NOT_AVAILABLE })}
        </span>
      </div>

      <span className="text-xs text-gray-600">
        {actorLabelOf(group.events[0])}
      </span>

      <ul className="mt-1 space-y-1">
        {group.events.map((event: ConciliationCaseEvent) => (
          <CaseEventLine
            key={event.id}
            event={event}
            justificationNames={justificationNames}
          />
        ))}
      </ul>

      {conciliation && (
        <CaseConciliationBlock
          event={conciliation}
          justificationNames={justificationNames}
          onOpenCase={onOpenCase}
        />
      )}
    </li>
  );
}
